'use client';

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Timestamp } from 'firebase/firestore';
import dayjs from 'dayjs';

import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { useDetailMode } from '../../../hooks/useDetailMode';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { clearPatternListCache } from '../../../hooks/usePatternList';
import useSnackbar from '../../../hooks/useSnackbar';

import { getPattern } from '../../../api/firebase/firestore/pattern/read';
import {
  getPatternHistories,
  rollbackPatternToHistory,
} from '../../../api/firebase/firestore/pattern/history';
import { aggregatePatternAccuracy } from '../../../api/firebase/function/patternAccuracyFunction';

import { PatternType } from '../../../types/pattern';
import { PatternHistory } from '../../../types/patternHistory';

import {
  calcAccuracyPercent,
  NO_HISTORY_KEY,
  type PatternHistoryAccuracyStats,
} from '../../../utils/pattern/accuracyCalc';

import {
  buttonTextIconLarge,
  gray10,
  gray40,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import { boldXxl, regularSm, spHeaderTitle } from '../../../constants/location';
import {
  PATTERN_ACCURACY_PAGE_TITLE,
  PATTERN_EDIT_HISTORY_PAGE_TITLE,
  PATTERN_ACCURACY_EMPTY_TEXT,
  PATTERN_EDIT_HISTORY_EMPTY_TEXT,
} from '../../../constants/labels';
import MetaTitle from '../../utils/MetaTitle';
import SideBar from '../../uiParts/menu/SideBar';
import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import PatternHistoryDiffModal from '../../uiParts/modal/PatternHistoryDiffModal';
import PatternRollbackConfirmModal from '../../uiParts/modal/PatternRollbackConfirmModal';

interface VersionRow {
  key: string;
  version: number | null;
  isCurrent: boolean;
  mode: 'custom' | 'standard' | null;
  updatedAt: Timestamp | null;
  executionCount: number;
  accuracyPercent: number | null;
  history: PatternHistory | null;
  /** v(N) と比較する前バージョン v(N-1)。null の場合は v1 */
  previousHistory: PatternHistory | null;
  /** ロールバックで作成された版の元バージョン番号（`change_note` から抽出） */
  rollbackSourceVersion: number | null;
}

/** `change_note` の `"vN の内容を適用"` から N を抽出する */
const parseRollbackSourceVersion = (changeNote: string | undefined): number | null => {
  if (!changeNote) return null;
  const match = changeNote.match(/^v(\d+)\s*の内容を適用/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
};

/** バージョン表示文字列を生成（現在 / ロールバック適用版に対応） */
const formatVersionLabel = (row: VersionRow): string => {
  if (row.version === null) return '履歴なし';
  const base = `v${row.version}`;
  const current = row.isCurrent ? '（現在）' : '';
  const rollback =
    row.rollbackSourceVersion !== null ? `（v${row.rollbackSourceVersion}を適用）` : '';
  return `${base}${current}${rollback}`;
};

const formatDate = (ts: Timestamp | null): string => {
  if (!ts) return '-';
  return dayjs(ts.seconds * 1000).format('YYYY/MM/DD HH:mm');
};

const formatAccuracy = (percent: number | null): string => {
  if (percent === null) return '-';
  return `${percent.toFixed(1)}%`;
};

const styles = {
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': { ...spHeaderTitle },
  },
  backButton: {
    marginRight: '8px',
  },
  tableSection: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: '0 32px',
    minHeight: '320px',
    padding: '16px 24px 56px',
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: 0,
      width: '100%',
    },
  },
  tableWrapper: { overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' },
  table: { minWidth: '720px', width: '100%' },
  tableHeadText: { ...regularSm, color: gray80, padding: '0 16px', whiteSpace: 'nowrap' as const },
  tableHeadRow: { height: '48px' },
  tableDataText: { ...regularSm, color: gray100, padding: '0 16px', whiteSpace: 'nowrap' as const },
  tableDataRow: { height: '48px' },
  legacyRow: { backgroundColor: gray10 },
  buttonHeight: { height: '32px' },
  metaInfo: {
    ...regularSm,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 32px 16px',
    color: gray80,
  },
  emptyText: {
    ...regularSm,
    color: gray80,
    padding: '32px 0',
    textAlign: 'center' as const,
  },
};

const PatternAccuracyVerification = (): ReactElement => {
  const { patternId } = useParams<{ patternId: string }>();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, isLoading: isAuthLoading } = useContext(AuthContext);
  const { isDetailView } = useDetailMode();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();

  const [pattern, setPattern] = useState<PatternType | null>(null);
  const [histories, setHistories] = useState<PatternHistory[]>([]);
  const [stats, setStats] = useState<Map<string, PatternHistoryAccuracyStats>>(new Map());
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [rollbackTarget, setRollbackTarget] = useState<PatternHistory | null>(null);
  const [rollbackLoading, setRollbackLoading] = useState<boolean>(false);
  const [diffTarget, setDiffTarget] = useState<{
    base: PatternHistory | null;
    target: PatternHistory;
  } | null>(null);

  // アクセス制御: 編集履歴はログイン済みの全ユーザーに開放（issue #2828）
  const canAccess = useMemo(() => !!user, [user]);

  // 運営スタッフ視点（処理枚数・精度・JSON 差分などの内部情報を表示するか）。
  // 運営スタッフでも詳細モードOFF時は顧客視点を再現するため非表示。
  const showStaffView = isDetailView;

  const pageTitle = showStaffView ? PATTERN_ACCURACY_PAGE_TITLE : PATTERN_EDIT_HISTORY_PAGE_TITLE;
  // 空状態テキストも役割別に出し分ける（運営: 編集履歴・OCR実行履歴 / 一般: 編集履歴のみ）
  const emptyText = showStaffView ? PATTERN_ACCURACY_EMPTY_TEXT : PATTERN_EDIT_HISTORY_EMPTY_TEXT;

  // 集計データの読み込み。
  // useSnackbar の setter は毎レンダーで参照が変わるため依存に含めず、
  // 純粋に「アクセス可」「patternId」「明示的な refreshKey」のみで再実行する。
  useEffect(() => {
    if (!canAccess || !patternId) return;
    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      try {
        // 精度集計（aggregatePatternAccuracy）はサーバ側で N+1 を回避しつつ集計する Callable。
        // issue #2828 で一般ユーザー（自社パターンのみ）にも開放済み。
        const [patternData, historyList, accuracyResp] = await Promise.all([
          getPattern(patternId),
          getPatternHistories(patternId),
          aggregatePatternAccuracy(patternId),
        ]);
        if (cancelled) return;
        setPattern(patternData);
        setHistories(historyList);
        const aggregated = new Map<string, PatternHistoryAccuracyStats>(
          Object.entries(accuracyResp.stats),
        );
        setStats(aggregated);
      } catch (error) {
        if (cancelled) return;
        setErrorText('データの取得に失敗しました');
        setSnackbarOpen(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [canAccess, patternId, refreshKey]);

  // 表示行を構築
  const rows = useMemo<VersionRow[]>(() => {
    // histories は version DESC で並んでいる想定
    const versionRows: VersionRow[] = histories.map((h, idx) => {
      const prev = histories[idx + 1] ?? null; // 一つ古い版（v(N-1)）
      const stat = stats.get(h.history_id);
      return {
        key: h.history_id,
        version: h.version,
        isCurrent: idx === 0,
        mode: h.use_custom_prompt ? 'custom' : 'standard',
        updatedAt: h.changed_at ?? null,
        executionCount: stat?.executionCount ?? 0,
        accuracyPercent: calcAccuracyPercent(stat),
        history: h,
        previousHistory: prev,
        rollbackSourceVersion: parseRollbackSourceVersion(h.change_note),
      };
    });

    // 履歴に紐づかない（レガシー）データ
    const legacyStat = stats.get(NO_HISTORY_KEY);
    if (legacyStat && legacyStat.executionCount > 0) {
      versionRows.push({
        key: NO_HISTORY_KEY,
        version: null,
        isCurrent: false,
        mode: null,
        updatedAt: null,
        executionCount: legacyStat.executionCount,
        accuracyPercent: null,
        history: null,
        previousHistory: null,
        rollbackSourceVersion: null,
      });
    }

    return versionRows;
  }, [histories, stats]);

  const handleRollback = async (): Promise<void> => {
    if (!rollbackTarget || !patternId || !user) return;
    setRollbackLoading(true);
    try {
      await rollbackPatternToHistory(patternId, rollbackTarget, user.uid, user.name || 'Unknown');
      clearPatternListCache();
      setInfoText(`v${rollbackTarget.version} の内容を適用しました`);
      setSnackbarOpen(true);
      setRollbackTarget(null);
      setRefreshKey(k => k + 1);
    } catch (error) {
      setErrorText('適用に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setRollbackLoading(false);
    }
  };

  if (isAuthLoading || !canAccess) {
    return <CustomLoading loading={true} />;
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle={pageTitle} />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          { '@media screen and (max-width:960px)': { paddingBottom: '84px' } },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton sx={styles.backButton} onClick={(): void => navigate('/pattern')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={styles.headerTitle}>{pageTitle}</Typography>
          </Box>
        </Box>

        <Box sx={styles.metaInfo}>
          <Typography sx={regularSm}>パターン名: {pattern?.pattern_name ?? '-'}</Typography>
        </Box>

        <Box sx={styles.tableSection}>
          {loading ? (
            <CustomLoading loading={true} />
          ) : rows.length === 0 ? (
            <Typography sx={styles.emptyText}>{emptyText}</Typography>
          ) : (
            <Box sx={styles.tableWrapper}>
              <Table sx={styles.table}>
                <TableHead>
                  <TableRow sx={styles.tableHeadRow}>
                    <TableCell sx={styles.tableHeadText}>バージョン</TableCell>
                    <TableCell sx={styles.tableHeadText}>モード</TableCell>
                    <TableCell sx={styles.tableHeadText}>更新日時</TableCell>
                    <TableCell sx={styles.tableHeadText}>処理枚数</TableCell>
                    <TableCell sx={styles.tableHeadText}>精度</TableCell>
                    <TableCell sx={styles.tableHeadText}>差分</TableCell>
                    <TableCell sx={styles.tableHeadText}>適用</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow
                      key={row.key}
                      sx={[styles.tableDataRow, row.version === null ? styles.legacyRow : null]}
                    >
                      <TableCell sx={styles.tableDataText}>{formatVersionLabel(row)}</TableCell>
                      <TableCell sx={styles.tableDataText}>
                        {row.mode === null ? '-' : row.mode === 'custom' ? 'カスタム' : '標準'}
                      </TableCell>
                      <TableCell sx={styles.tableDataText}>{formatDate(row.updatedAt)}</TableCell>
                      <TableCell sx={styles.tableDataText}>{row.executionCount}枚</TableCell>
                      <TableCell sx={styles.tableDataText}>
                        <Tooltip
                          title={
                            row.accuracyPercent === null
                              ? '集計対象データがありません'
                              : '確認済み帳票の精度（直さずに済んだ文字数 / 正解データの文字数）を、帳票1枚あたりで平均した値'
                          }
                        >
                          <span>{formatAccuracy(row.accuracyPercent)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={styles.tableDataText}>
                        {row.history && row.previousHistory ? (
                          <Button
                            sx={[secondaryButtonBackgroundColor, styles.buttonHeight]}
                            onClick={(): void => {
                              if (!row.history) return;
                              setDiffTarget({
                                base: row.previousHistory,
                                target: row.history,
                              });
                            }}
                          >
                            確認
                          </Button>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell sx={styles.tableDataText}>
                        {row.history && !row.isCurrent ? (
                          <Button
                            sx={[primaryButtonBackgroundColor, styles.buttonHeight]}
                            onClick={(): void => setRollbackTarget(row.history)}
                          >
                            <Typography sx={buttonTextIconLarge} fontSize="12px">
                              適用
                            </Typography>
                          </Button>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />

      {diffTarget && (
        <PatternHistoryDiffModal
          open={!!diffTarget}
          onClose={(): void => setDiffTarget(null)}
          baseHistory={diffTarget.base}
          targetHistory={diffTarget.target}
          showJsonMode={showStaffView}
        />
      )}

      <PatternRollbackConfirmModal
        open={!!rollbackTarget}
        onClose={(): void => setRollbackTarget(null)}
        onApply={handleRollback}
        loading={rollbackLoading}
        versionLabel={rollbackTarget ? `v${rollbackTarget.version}` : ''}
      />
    </Box>
  );
};

export default PatternAccuracyVerification;
